import { UserInfoType } from '@/interface/KKC';
import type { QueryLiveActivityNodeResponse } from '@/interface/liveActivity';
import { NewUserActivityInfo } from '@/interface/pub';
import * as Sentry from '@sentry/react';
import request, { Options } from '@web/common/src/utils/requests/request';
import { ClInterestRateRange } from './interface/CL/clCredit';
import { BoolString } from './interface/KKC/withdrawal';

// 获取用户信息

export const getUserInfoApi = (options?: Options) =>
  request.post<UserInfoType>('/user/getUserInfo', null, options).then((res) => {
    Sentry.setUser({
      id: res.userId,
      username: res.userName,
    });
    const scope = Sentry.getCurrentScope();
    scope.setUser({
      id: res.userId,
      username: res.userName,
    });
    return res;
  });

// 判断是否是真实通过Ekyc，当身份证和人脸匹配通过 ||  匹配不通过但是 Ekyc审批通过了，则返回 Y；否则是 N
export const queryRealEkyc = () =>
  request.post<{
    isRealEkycApproval: BoolString; // 是否通过Ekyc Y 是 N 否
    isPayNewFlow: BoolString; // 是否走提现新流程 Y 是 N 否
  }>('credit/clCredit/queryRealEkyc');

// 授信流程退出时调用的接口
export const processExitRecord = () =>
  request.post('/credit/apply/processExitRecord', null, { loading: true });

// CL 授信流程退出时调用的接口
export const clProcessExitRecord = () =>
  request.post('/credit/apply/clProcessExitRecord', null, { loading: true, isIntercept: false });

export const commonConfig = (keys: string) =>
  request.post<{
    configs: {
      [key: string]: string;
    };
  }>('/credit/nl/common/config', { keys });

export const queryClAvaliable = (userId: string) =>
  request.get<BoolString>(`credit/approve/cl/WhiteList/${userId}`);

// 查詢用戶是否白名單
export const queryUserAvaliable = (userId: string, type: string) =>
  request.get<BoolString>(`credit/approve/whiteList/${userId}/${type}`);

// Facebook CAPI
export const trackFacebookCapi = (params: any) =>
  request.post<BoolString>('/credit/nl/common/faceBookApi', params);

// 获取AbTest结果 - 根据场景和用户ID
export const commonABtest = (bizId: string, scene: string) =>
  request.get<{ bizId: string; scene: string; env: string }>(
    `/credit/nl/common/abtest/user/env/${bizId}/${scene}`
  );

// byClaude-start
// 获取不免密AbTest结果 - 根据场景和用户ID
export const commonABtestNoEncrypt = (userId: string, scene: string) =>
  request.get<{ enabled: 'Y' | 'N' }>(`/credit/abtest/user/${userId}/${scene}`);
// byClaude-end

// 查詢公司配置
export const queryComConfig = () => request.get(`credit/layout-data/query-statement-info`);

// 查詢產品配置
export const queryProductConfig = (productCode?: string) =>
  request.get<ClInterestRateRange>(
    `credit/nl/common/query-product-info?productCode=${productCode}`
  );
// 获取oss上传key
export const generationkey = () => request.get(`/system/oss/generation/key/upload/06`);

// byClaude-start 2026-07-23 13:59:33
/**
 * OSS 文件上传（后端代理上传）
 * @param file 待上传的文件
 * @param moduleCode 模块代码，默认 '06'
 */
export const ossUpload = (file: File, moduleCode = '06') => {
  const formData = new FormData();
  formData.append('file', file);
  return request.post<{
    fileKey: string;
    fileId: string;
    fileName: string;
    filePath: string;
    moduleCode: string;
    name: string;
    uid: string;
    status: string;
  }>(`credit/oss/upload/${moduleCode}`, formData);
};
// byClaude-end

// byClaude-start 2026-07-23 16:30:00
/**
 * OSS 文件访问路径响应结构
 */
export interface OssFilePathResponse {
  /** 可直接访问的文件 URL（签名 URL） */
  filePath: string;
}

/**
 * 获取 OSS 文件访问路径（用于预览私有桶文件）
 * @param filePath OSS 文件相对路径
 */
export const getOssFilePath = (filePath: string) =>
  request.get<OssFilePathResponse>(
    `credit/oss/get-all-file-path?filePath=${encodeURIComponent(filePath)}`
  );
// byClaude-end

// byClaude-start
// 查詢貸款期數字典枚舉
// byClaude 2026-06-18 18:53:52
export interface QueryLoanMaturityParams {
  language: string;
  type: string;
  parentCode: string | null;
  tag: string | null;
  userId?: string | number | null;
}

export interface LoanMaturityDictItem {
  dictCode: string;
  dictLabel: string;
  dictValue: string;
}

export const queryLoanMaturity = (params: QueryLoanMaturityParams) =>
  request.post<LoanMaturityDictItem[]>(
    'credit/nl/common/code/query-by-type-parent-language-tag',
    params
  );
// byClaude-end

// 新客限時優惠活動進度查詢
export const queryNewUserProgress = (params: {
  userId: string;
  creditPassFlag?: BoolString;
  creditApplyId?: string;
}) => request.post<NewUserActivityInfo>(`/credit/new-activity/query-new-customer-progress`, params);

export const queryLiveActivityNode = (params: {
  userId: string;
  applyId: string;
  bizType: 'CL_CREDIT' | 'KKC_CREDIT';
  triggerType: string;
  querySource: 'H5';
}) =>
  request.post<QueryLiveActivityNodeResponse>(
    '/credit/live-activity/query-node-by-condition',
    params
  );

// 查詢用戶是否統一還款日白名單
export const queryUserUnionLoanAvaliable = () =>
  request.get<{ enabled: BoolString }>(`credit/pay/getUniformRepaymentDateWhiteList`);

// TU重定向
export interface TURedirectResponse {
  redirectUrl: string;
  encryptedData: string;
  formHtml: string;
}

export const generateTURedirect = (
  userId: string,
  src: 'W' | 'A',
  entranceCode: string,
  lang: string
) =>
  request.post<TURedirectResponse>('credit/app/creditview/generate-redirect', {
    userId,
    src,
    entranceCode,
    lang,
  });

export interface LastedRecordResponse {
  canQuery: 'Y' | 'N';
  [key: string]: any;
}

export const queryLastedRecord = (options?: Options) =>
  request.get<LastedRecordResponse>('credit/app/creditview/record/queryLastedRecord', null, {
    loading: true,
    isIntercept: options?.isIntercept,
  });

export const getEkycState = () =>
  request.post<{
    ekycLockEnd: string; // ekyc禁申截止日期
    ekycType: string; // ekyc类型
    isNeedEkyc: string; // 是否需要ekyc弹窗
    isPass: string; // ekyc是否通过
    message: string; // 文案提示
  }>('credit/apply/ekyc/state');

/**
 * 解析报告请求响应结构
 
 */
export interface ParseReportRequestResponse {
  fileKey: string;
}

/**
 * 解析报告结果响应结构
 
 */
export interface ParseResultResponse {
  fileKey: string;
  status: 'IN_PROGRESS' | 'SUCCESS' | 'FAIL';
  errorMessage: string;
}

/**
 * 请求解析信贷报告
 
 * @param filePath OSS文件路径
 * @returns 返回 fileKey
 */
export const parseReportRequest = (filePath: string) =>
  request.post<ParseReportRequestResponse>('credit/creditview-report/parse-report-request', {
    filePath,
  });

/**
 * 查询报告解析结果
 
 * @param params.fileKey 文件标识
 * @returns 返回解析状态和结果
 */
export const parseResult = (params: { fileKey: string }) =>
  request.post<ParseResultResponse>('credit/creditview-report/parse-result', params);

export const queryPageState = () => request.get('credit/creditview-report/page-state');

/**
 * byClaude-start
 * Google评分报告检查响应结构
 */
export interface CheckGoogleScoreReportResponse {
  needTracking: boolean;
  scoreValue: number | null;
  scoreTime: string | null;
  userId: number;
}

/**
 * 检查用户是否需要上报Google评分报告
 * @param userId 用户ID
 */
export const checkGoogleScoreReport = (userId: string) =>
  request.get<CheckGoogleScoreReportResponse>(`credit/moat/checkGoogleScoreReport/${userId}`);
// byClaude-end

// Contains claude-generated edits.
